import { Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { AiGuardNoticeList } from "@/components/ai/ai-guard-notice-list";
import type {
  AiAccessPromptView,
  AiGuardNoticeView,
  ContinuationSuggestionSessionView,
} from "@/features/ai";

type ContinuationSuggestionsPanelProps = {
  deleteBusyId?: string | null;
  busy?: boolean;
  errorMessage?: string | null;
  guardNotices?: AiGuardNoticeView[];
  onDeleteSuggestionSet?: (suggestionSetId: string) => void;
  onPromptAction?: () => void;
  onRequestSuggestions?: () => void;
  prompt?: AiAccessPromptView | null;
  sessions?: ContinuationSuggestionSessionView[];
  statusLabel?: string;
};

export function ContinuationSuggestionsPanel({
  deleteBusyId = null,
  busy = false,
  errorMessage = null,
  guardNotices = [],
  onDeleteSuggestionSet,
  onPromptAction,
  onRequestSuggestions,
  prompt = null,
  sessions = [],
  statusLabel,
}: ContinuationSuggestionsPanelProps) {
  const { t } = useTranslation();
  const requestBlocked =
    prompt?.kind === "budget-restricted" || prompt?.kind === "wait";

  return (
    <section className="space-y-4">
      <div className="space-y-1">
        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
          {t("story.ai.continuation.eyebrow")}
        </p>
        <h3 className="text-xl text-foreground">{t("story.ai.continuation.title")}</h3>
        <p className="text-sm leading-6 text-muted-foreground">
          {t("story.ai.continuation.body")}
        </p>
      </div>

      {errorMessage ? (
        <div
          className="rounded-[1.1rem] border border-destructive/25 bg-destructive/5 px-4 py-4 text-sm text-destructive"
          role="alert"
        >
          {errorMessage}
        </div>
      ) : null}

      {prompt ? (
        <div className="space-y-3 rounded-[1.1rem] border border-amber-300/40 bg-amber-50/90 px-4 py-4 text-sm text-amber-950">
          <p>{t(prompt.descriptionKey, prompt.descriptionValues)}</p>
          {prompt.kind === "budget-restricted" ? (
            <p className="text-xs text-amber-900/80">{t("story.ai.access.creditsSafe")}</p>
          ) : null}
          {prompt.ctaLabelKey && onPromptAction ? (
            <Button onClick={onPromptAction} size="sm" type="button" variant="outline">
              {t(prompt.ctaLabelKey)}
            </Button>
          ) : null}
        </div>
      ) : null}

      <AiGuardNoticeList notices={guardNotices} />

      {sessions.length === 0 ? (
        <div className="rounded-[1.1rem] border border-dashed border-ink/10 bg-[#f6eee1]/65 px-4 py-4 text-sm text-muted-foreground">
          {busy
            ? t("story.ai.continuation.loading")
            : t("story.ai.continuation.empty")}
        </div>
      ) : (
        <div className="space-y-3">
          {sessions.map((session) => (
            <article
              key={session.id}
              className="space-y-3 rounded-[1.15rem] border border-ink/10 bg-[#f6eee1]/72 px-4 py-4"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="space-y-1">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    {t("story.ai.continuation.generatedAt")}
                  </p>
                  <p className="text-sm text-foreground">
                    {new Date(session.createdAt).toLocaleString()}
                  </p>
                </div>
                <Button
                  disabled={deleteBusyId === session.id}
                  onClick={() => {
                    if (
                      !onDeleteSuggestionSet
                      || !window.confirm(t("story.ai.continuation.deleteConfirm"))
                    ) {
                      return;
                    }

                    onDeleteSuggestionSet(session.id);
                  }}
                  size="sm"
                  type="button"
                  variant="ghost"
                >
                  {deleteBusyId === session.id
                    ? t("story.ai.continuation.deleting")
                    : t("story.ai.continuation.deleteAction")}
                </Button>
              </div>
              <ol className="space-y-2">
                {session.options.map((option, index) => (
                  <li
                    key={option.id}
                    className="rounded-[1rem] border border-white/80 bg-white/88 px-3 py-3 text-sm leading-6 text-foreground"
                  >
                    <span className="mr-2 text-xs font-semibold uppercase tracking-[0.16em] text-clay">
                      {t("story.ai.continuation.optionLabel", { count: index + 1 })}
                    </span>
                    {option.text}
                  </li>
                ))}
              </ol>
            </article>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between gap-3 border-t border-ink/10 pt-4">
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          {statusLabel ?? t("story.ai.continuation.freeUsesRemaining", { count: 5 })}
        </span>
        <Button
          disabled={busy || requestBlocked}
          onClick={onRequestSuggestions}
          type="button"
          variant="outline"
        >
          <Sparkles className="h-4 w-4" />
          {busy
            ? t("story.ai.continuation.requesting")
            : t("story.ai.continuation.requestAction")}
        </Button>
      </div>
    </section>
  );
}
